// widget/src/hooks/useDrawerOpen.ts
//
// Tracks whether a storefront drawer OR the mobile menu is open, so the Pearl
// chip (and proactive preview) hides while one covers the page — the same rule
// the Shopify chat uses (theme: assets/shopify-chat-fix.js). The DYC theme sets
// a body class for each full-screen overlay:
//   drawers → cart-drawer-open · nf-drawer-open (nav-finder paint search) ·
//             mcc-drawer-open · info-drawer-open · drawer-open (generic)
//   menus   → mobile-menu-open (header menu, global.js) ·
//             ab-menu-open (allbirds floating header)
// We match the shared "drawer-open" AND "menu-open" suffixes so any of them
// counts. The menu classes only ever land on <body> for the full-screen mobile
// overlay — desktop submenu hover sets "menu-open"/"submenu-open" on CHILD
// elements, never <body> — so matching on the body is safe and won't over-hide
// on desktop. We do NOT key off `scroll-locked` — many small modals set it and
// that would over-hide (shopify-chat-fix.js makes the same call).

import { useEffect, useState } from "react"

// Body-class suffixes that mean "a full-screen storefront overlay is covering
// the page": every drawer variant (*-drawer-open) + the mobile menu
// (mobile-menu-open / ab-menu-open). Substring match keeps it to two checks.
const OVERLAY_CLASS_SUFFIXES = ["drawer-open", "menu-open"]

function anyDrawerOpen(): boolean {
  if (typeof document === "undefined") return false
  const cls = document.body.classList
  for (let i = 0; i < cls.length; i++) {
    const name = cls[i]
    if (OVERLAY_CLASS_SUFFIXES.some((s) => name.indexOf(s) !== -1)) return true
  }
  return false
}

export function useDrawerOpen(): boolean {
  const [open, setOpen] = useState(anyDrawerOpen)

  useEffect(() => {
    if (typeof document === "undefined") return
    const update = () => setOpen(anyDrawerOpen())
    update()
    const observer = new MutationObserver(update)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    })
    return () => observer.disconnect()
  }, [])

  return open
}
